import { EndUser, EndUserConstants, EndUserProxySettings, EndUserError, KSPSettings, EndUserCertificate, EndUserKeyMedia, EndUserSignContainerInfo, EndUserCertificateInfoEx, EndUserTaxReport } from "../euscp/euscp";
import { DigitalSignatureKeyType, PrivateKeyInfo, FilePrivateKeyInfo, HardwarePrivateKeyInfo, KspPrivateKeyInfo, EndUserKSP, KSPKeyMediasResponse, KSPUserAuthData, KSPPrivateKeyAuthData, DigitalSignatureKeyTypeWindowsCertStore, ProtectTaxReportParams } from "./Models";
import { arrayToBase64, base64ToArray, convertToBase64, dateWithoutTimezone, downloadAndSignFiles, downloadData, format, isDefaultValue, readFile } from "./Utils";
import UmCAService from "./umcaservice";
import Resourses from "./Resourses.json";
import GlSign from "./GlSign";

const PRIVATE_KEY_TYPE = "PKType";
const PRIVATE_KEY_INFO = "PKInfo";

export default class DigitalSignature {
  /**
   * @param {DigitalSignatureSettings} settingsProvider
   */
  constructor(settingsProvider) {
    this._settingsProvider = settingsProvider;

    /** @type {GlSign} */
    this._glSign = null;

    /** @type {DigitalSignatureSettings} */
    this._settings = null;

    /** @type {EndUser} */
    this._euSignFile;
    /** @type {EndUser} */
    this._euSignKeyMedia;

    /** @type {EndUser} */
    this._euSign = null;

    /** @type {CASettings} */
    this._selectedCA = null;

    /** @type {KSPSettings} */
    this._KSPs = [];

    /** @type {PrivateKeyInfo} */
    this._readedKey = null;
  }

  /**
   * Считанный приватный ключ
   * @type {PrivateKeyInfo}
   */
  get readedKey() {
    return this._readedKey;
  }

  get _selectedIssuerCN() {
    return this._selectedCA ? this._selectedCA.issuerCNs[0] : null;
  }

  getLibraryType() {
    return this.keyType;
  }

  /**
   * Сменить тип библиотеки
   * @param {number} type
   */
  async setLibraryType(type) {
    switch (type) {
      case DigitalSignatureKeyType.Token:
        if (this._euSign === this._euSignKeyMedia && await this._euSign.IsInitialized()) {
          return;
        }
        this._euSign = this._euSignKeyMedia;
        break;
      case DigitalSignatureKeyType.UmCAService:
        this._euSign = this._euSignFile;
        break;
      default:
        if (this._euSign === this._euSignFile) {
          return;
        }
        this._euSign = this._euSignFile;
        break;
    }

    if (type != null && type != undefined) {
      this._preferredKeyType = type;
    }
    this.keyType = type;
    await this.initialise();
  }

  /**
   * Инициализировать библиотеку
   * @returns {Promise<number>} Текущий тип библиотеки
   */
  async initialise() {
    if (!this._glSign) {
      this._glSign = await Promise.resolve(this._settingsProvider.getGlSign());
    }

    if (!this._settings) {
      this._settings = await Promise.resolve(this._settingsProvider.getSettings(this._glSign.AllowTestKeys));
    }

    if (!this._euSignFile) {
      this._euSignFile = new EndUser(this._settings.libraryUrl, EndUserConstants.EndUserLibraryType.JS, this._glSign.WebClientFileSize);
    }

    if (!this._euSignKeyMedia) {
      this._euSignKeyMedia = new EndUser(this._settings.libraryUrl, EndUserConstants.EndUserLibraryType.SW, this._glSign.WebClientFileSize);
    }

    if (!this._euSign) {
      this._euSign = this._glSign.PreferHarware ? this._euSignKeyMedia : this._euSignFile;
      this.keyType = this._glSign.PreferHarware ? DigitalSignatureKeyType.Token : this._preferredKeyType;
    }

    if (this.keyType === DigitalSignatureKeyType.UmCAService) {
      if (!this.UmCAService) {
        this.UmCAService = new UmCAService(this.glSign.UmcaservicePort);
      }
      if (!await this.UmCAService.tryConnect()) {
        throw {
          code: EndUserError.EU_ERROR_LIBRARY_LOAD,
          message: this._resourses.UmCAServiceConnectError
        };
      }
    }

    const euSign = this._euSign;

    if (euSign === this._euSignKeyMedia) {
      const library = await euSign.GetLibraryInfo();

      if (!library.supported) {
        throw {
          code: EndUserError.EU_ERROR_LIBRARY_LOAD,
          message: this._resourses.LibraryNotSupported
        };
      }

      if (!library.loaded) {
        // Бібліотека встановлена, але потребує оновлення
        if (library.isNativeLibraryNeedUpdate) {
          throw {
            code: EndUserError.EU_ERROR_LIBRARY_LOAD,
            message: this._resourses.LibraryNeedsUpdate,
            reload: true,
            urls: [
              { href: library.nativeLibraryUpdateURL, caption: this._resourses.LibraryUpdate },
              { href: library.helpURL, caption: this._resourses.LibraryUserManual }
            ]
          };
        }

        // Якщо браузер підтримує веб-розширення рекомендується
        // додатково до нативних модулів встановлювати веб-розширення
        // Увага! Встановлення веб-розширень ОБОВ'ЯЗКОВЕ для ОС Linux та ОС Windows Server
        if (library.isWebExtensionSupported &&
          !library.isWebExtensionInstalled) {
          throw {
            code: EndUserError.EU_ERROR_LIBRARY_LOAD,
            message: this._resourses.LibraryNeedsExtensionInstall,
            reload: true,
            urls: [
              { href: library.webExtensionInstallURL, caption: this._resourses.WebExtensionInstall },
              { href: library.helpURL, caption: this._resourses.LibraryUserManual }
            ]
          };
        }

        // Бібліотека (нативні модулі) не встановлені
        throw {
          code: EndUserError.EU_ERROR_LIBRARY_LOAD,
          message: this._resourses.LibraryNeedsInstall,
          reload: true,
          urls: [
            { href: library.nativeLibraryInstallURL, caption: this._resourses.LibraryInstall },
            { href: library.helpURL, caption: this._resourses.LibraryUserManual }
          ]
        };
      }
    }

    const isInitialised = await euSign.IsInitialized();
    if (isInitialised) {
      return this.getLibraryType();
    }

    if (!this._euSettings) {
      let certificates;
      try {
        certificates = await this._settings.certificatesProvider.loadCertificates();
      } catch (error) {
        if (error && error.code === EndUserError.EU_ERROR_DOWNLOAD_FILE) {
          throw {
            code: EndUserError.EU_ERROR_DOWNLOAD_FILE,
            message: this._resourses.DownloadingRootCertificatesError
          };
        } else {
          throw error;
        }
      }

      this._euSettings = {
        language: this._language,
        encoding: "UTF-16LE",
        httpProxyServiceURL: this._settings.httpProxyServiceURL,
        directAccess: this._glSign.DirectAccess,
        CAs: certificates.CAs,
        CACertificates: certificates.CACertificates,
        KSPs: this.KSPs,
        allowedKeyMediaTypes: [
          "е.ключ BIFIT iToken",
          "криптомод. ІІТ Гряда-61",
          "е.ключ ІІТ Алмаз-1К",
          "е.ключ ІІТ Алмаз-1К (Bluetooth)",
          "е.ключ ІІТ Кристал-1",
          "криптомод. ІІТ Гряда-301",
          "ID-карта громадянина (БЕН)",
          "е.ключ ІІТ Алмаз-1К (PKCS#11)",
          "е.ключ ІІТ Кристал-1 (PKCS#11)",
          "е.ключ чи смарт-карта Avest (PKCS#11)",
          "е.ключ Ефіт Key (PKCS#11)",
          "е.ключ чи смарт-карта Автор (PKCS#11)",
          "е.ключ чи смарт-карта Автор 338 (PKCS#11)",
          "смарт-карта Техноконс. TEllipse3 (PKCS#11)",
          "смарт-карта Техноконс. TEllipse",
          "смарт-карта Техноконс. TEllipse2/3",
          "е.ключ SIS DIAMOND",
          "е.ключ SafeNet iKey (PKCS#11, RSA)",
          "е.ключ Aruba Key (PKCS#11, RSA)"
        ]
      };
    }

    await euSign.Initialize(this._euSettings);
    if (euSign === this._euSignKeyMedia && !await this._euSignFile.IsInitialized()) {
      this._euSignFile.Initialize(this._euSettings);
    }
    if (this._glSign.ApplyProxySettings) {
      const { UseProxy, ProxyAddress, ProxyPort, ProxyUser, ProxyPassword } = this._glSign;
      const proxy = new EndUserProxySettings();
      proxy.useProxy = UseProxy;
      proxy.address = ProxyAddress;
      proxy.port = ProxyPort;
      proxy.anonymous = !ProxyUser || !ProxyPassword;
      proxy.user = ProxyUser;
      proxy.password = ProxyPassword;
      proxy.savePassword = true;
      await euSign.SetProxySettings(proxy);
      if (euSign === this._euSignKeyMedia && !await this._euSignFile.IsInitialized()) {
        this._euSignFile.SetProxySettings(proxy);
      }
    }

    await euSign.SetRuntimeParameter(EndUserConstants.EU_SIGN_TYPE_PARAMETER, EndUserConstants.EndUserSignType.CAdES_X_Long);
    if (euSign === this._euSignKeyMedia && !await this._euSignFile.IsInitialized()) {
      this._euSignFile.SetRuntimeParameter(EndUserConstants.EU_SIGN_TYPE_PARAMETER, EndUserConstants.EndUserSignType.CAdES_X_Long);
    }

    return this.getLibraryType();
  }

  /**
   * Значение ПГУ GlSign
   * @type {GlSign}
   */
  get glSign() {
    return this._glSign;
  }

  async setLanguage(language) {
    await this._euSignFile.SetLanguage(language);
    if (await this._euSignKeyMedia.IsInitialized()) {
      await this._euSignKeyMedia.SetLanguage(language);
    }
  }

  /**
   * Распарсить сертификат
   * @param {Uint8Array|string} cert
   * @returns {Promise<EndUserCertificate>}
   */
  async parseCertificate(cert) {
    await this.setLibraryType(DigitalSignatureKeyType.File);

    if (typeof cert == "string") {
      cert = base64ToArray(cert);
    }

    const res = new EndUserCertificate();
    res.data = cert;
    res.infoEx = await this._euSign.m_library.m_worker.postMessage("ParseCertificateEx", [cert]);

    return res;
  }

  /**
   * Получить список подключённых устройств
   * @returns {Promise<Array<EndUserKeyMedia>>} Список подключённых устройств
   */
  async getKeyMedias() {
    await this.setLibraryType(DigitalSignatureKeyType.Token);
    return this._euSignKeyMedia.GetKeyMedias();
  }

  /**
   * Считать ключ по параметрам
   * @param {PrivateKeyInfo} keyInfo - Параметры ключа
   */
  async readPrivateKeyByInfo(keyInfo) {
    if (!keyInfo) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.BadParameter + " keyInfo"
      };
    }

    await this.setLibraryType(keyInfo.keyType);
    switch (keyInfo.keyType) {
      case DigitalSignatureKeyType.File:
        return await this.readFileKey(keyInfo.privateKey, keyInfo.password, keyInfo.certificates);
      case DigitalSignatureKeyType.Token:
        return await this.readHardwareKey(keyInfo.keyMedia, keyInfo.certificates);
      case DigitalSignatureKeyType.KSP:
        return await this.readPrivateKeyKSP(keyInfo.ksp, keyInfo.userId, false, keyInfo.keyId);
      case DigitalSignatureKeyType.UmCAService:
        return await this.readPrivateKeyUmCAService(keyInfo.keyMedia);
      case DigitalSignatureKeyType.WindowsCertStore:
        return await this.readWindowsCertStorePrivateKey();
      default:
        return null;
    }
  }

  /**
   * Считать аппаратный ключ
   * @param {EndUserKeyMedia} keyMedia - Параметры аппаратного ключа
   * @param {Array<Uint8Array>?} certs - Сертификаты ключа (при необходимости)
   */
  async readHardwareKey(keyMedia, certs) {
    await this.setLibraryType(DigitalSignatureKeyType.Token);

    if (!keyMedia) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.BadParameter + " keyMedia"
      };
    }
    if (!keyMedia.password) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.PasswordNotSet
      };
    }

    if (Array.isArray(certs) && certs.length > 0) {
      certs = await Promise.all(certs.map(cert =>
        cert instanceof File || cert instanceof Blob? readFile(cert) 
        : cert instanceof EndUserCertificate ? cert.data
        : cert));
    } else {
      certs = null;
    }

    try {
      const ownerInfo = await this._euSign.ReadPrivateKey(keyMedia, certs, this._selectedIssuerCN);
      this._readedKey = new HardwarePrivateKeyInfo(
        DigitalSignatureKeyType.Token,
        ownerInfo,
        await this._euSign.GetOwnCertificates(),
        keyMedia
      );
      this._readedKey._glSign = this._glSign;
      return this._readedKey;
    } catch (error) {
      if (error && error.code === EndUserError.EU_ERROR_CERT_NOT_FOUND) {
        if (!this._selectedIssuerCN) {
          throw {
            code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
            message: this._resourses.ReadPrivateKeyCAAutoDetectError
          };
        } else if (this._selectedIssuerCN) {
          if (!this._selectedCA.cmpAddress) {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyNeedCertificateError, this._selectedIssuerCN)
            };
          } else {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyInvalidCAError, this._selectedIssuerCN)
            };
          }
        }
      }
      throw error;
    }
  }

  /**
   * Считать файловый ключ
   * @param {Uint8Array|File} privateKey - Файловый ключ
   * @param {string} password - Пароль от ключа
   * @param {Array<Uint8Array|File>?} certs - Сертификаты ключа (при необходимости)
   */
  async readFileKey(privateKey, password, certs) {
    await this.setLibraryType(DigitalSignatureKeyType.File);

    if (!privateKey) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.BadParameter + " keyMedia"
      };
    }
    if (!password) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.PasswordNotSet
      };
    }

    if (privateKey instanceof File || privateKey instanceof Blob) {
      privateKey = await readFile(privateKey);
    }

    if (Array.isArray(certs) && certs.length > 0) {
      certs = await Promise.all(certs.map(cert =>
        cert instanceof File || cert instanceof Blob ? readFile(cert) 
        : cert instanceof EndUserCertificate ? cert.data
        : cert));
    } else {
      certs = null;
    }

    try {
      const ownerInfo = await this._euSign.ReadPrivateKeyBinary(privateKey, password, certs, this._selectedIssuerCN);
      this._readedKey = new FilePrivateKeyInfo(
        DigitalSignatureKeyType.File,
        ownerInfo,
        await this._euSign.GetOwnCertificates(),
        privateKey,
        password
      );
      this._readedKey._glSign = this._glSign;
      return this._readedKey;
    } catch (error) {
      if (error && error.code === EndUserError.EU_ERROR_CERT_NOT_FOUND) {
        if (!this._selectedIssuerCN) {
          throw {
            code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
            message: this._resourses.ReadPrivateKeyCAAutoDetectError
          };
        } else if (this._selectedIssuerCN) {
          if (!this._selectedCA.cmpAddress) {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyNeedCertificateError, this._selectedIssuerCN)
            };
          } else {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyInvalidCAError, this._selectedIssuerCN)
            };
          }
        }
      }
      throw error;
    }
  }

  /**
   * Получить список ключей из jks контейнера
   * @param {Uint8Array|File} jks - JKS контейнер
   * @returns {Promise<EndUserJKSPrivateKeyInfo[]>}
   */
  async getJKSPrivateKeys(jks) {
    await this.setLibraryType(DigitalSignatureKeyType.File);

    if (jks instanceof File || jks instanceof Blob) {
      jks = await readFile(jks);
    }
    return await this._euSign.GetJKSPrivateKeys(jks);
  }

  /**
   * Проверить, является ли файл JKS контейнером
   * @param {File} file файл с ключом
   */
  isJKSContainer(file) {
    return file.name.endsWith(".jks");
  }

  /**
     * Считать ключ с DepositSign
     * @param {string} userId - Идентификатор пользователя
     * @param {boolean?} getCerts - Получать информацию о ключе пользователя. Это приведёт к дополнительному запросу на подписание
     */
  async readPrivateKeyDepositsign(userId, getCerts = false) {
    const deposit = this.KSPs.find(ksp => ksp.id === "DEPOSITSIGN");
    return await this.readPrivateKeyKSP(deposit, userId, getCerts);
  }

  /**
   * Считать ключ с Дiя
   * @param {boolean?} getCerts - Получать информацию о ключе пользователя. Это приведёт к дополнительному запросу на подписание
   */
  async readPrivateKeyDiia(getCerts = false) {
    const diia = this.KSPs.find(ksp => ksp.id === "DIIA-SIGN");
    return await this.readPrivateKeyKSP(diia, null, getCerts);
  }

  /**
   * Считать ключ с облачного провайдера
   * @param {KSPSettings} ksp - Идентификатор облачного провайдера
   * @param {string?} userId - Идентификатор пользователя
   * @param {boolean?} getCerts - Получать информацию о ключе пользователя. Это приведёт к дополнительному запросу на подписание
   * @param {string?} keyId Идентификатор ключа
   * @param {KSPPrivateKeyAuthData?} authData Параметры авторизации ключа
   * @param {Array<Uint8Array|File>?} certs - Сертификаты ключа (при необходимости)
   */
  async readPrivateKeyKSP(ksp, userId, getCerts = false, keyId, authData, certs) {
    await this.setLibraryType(DigitalSignatureKeyType.KSP);

    if (ksp.ksp === EndUserConstants.EU_KSP_DIIA) {
      await this._diiaCheckAccess(ksp);
    }

    if (Array.isArray(certs) && certs.length > 0) {
      certs = await Promise.all(certs.map(cert =>
        cert instanceof File || cert instanceof Blob? readFile(cert) 
        : cert instanceof EndUserCertificate ? cert.data
        : cert));
    } else {
      certs = null;
    }

    const ownerInfo = await this._euSign.ReadPrivateKeyKSP(userId, ksp.name, getCerts, keyId, null, authData, certs);
    if ((getCerts || certs) && !ownerInfo) {
      throw {
        code: EndUserError.EU_ERROR_KEY_MEDIAS_READ_FAILED,
        message: this._resourses.PrivateKeyNotReaded
      };
    }
    this._readedKey = new KspPrivateKeyInfo(
      DigitalSignatureKeyType.KSP,
      ownerInfo,
      (getCerts || certs) ? await this._euSign.GetOwnCertificates() : [],
      userId,
      ksp,
      keyId
    );
    this._readedKey._glSign = this._glSign;
    return this._readedKey;
  }

  /**
   * Получить список ключей у пользователя облачного провайдера
   * @param {KSPSettings} kspSettings Облачный провайдер
   * @param {KSPUserAuthData} authData
   * @returns {KSPKeyMediasResponse}
   */
  async getKeyMediasKSP(kspSettings, authData) {
    await this.setLibraryType(DigitalSignatureKeyType.KSP);

    return this._euSign.m_library.m_worker.postMessage("GetKeyMediasKSP", [kspSettings, authData]);
  }

  /**
   * Установить код вухфакторной авторизации для подписания через провайдер облачных ключей
   * @param {string} code Код двухфакторной авторизации
   */
  async setTwoFactorCodeKSP(code) {
    await this.setLibraryType(DigitalSignatureKeyType.KSP);

    return this._euSign.m_library.m_worker.postMessage("SetTwoFactorCodeKSP", [code]);
  }

  /**
   * Коллбэк для идентификации
   * @callback authenticationCallback
   * @param {EndUserConfirmKSPOperationEvent} data - Данные для идентификации (Qr-код, ссылка и тд.)
   */
  /**
   * Добавить коллбэк идентификации для облачных провайдеров
   * @param {authenticationCallback} event - Коллбэк. Вызывается при запросе на подписание.
   */
  async addConfirmKSPOperationEventListener(event) {
    await this._euSignFile.AddEventListener(EndUserConstants.EndUserEventType.ConfirmKSPOperation, function (data) {
      data.qrCode = "data:image/bmp;base64," + data.qrCode;
      event(data);
    });
  }

  /**
   * Считать ключ с помощью UmCAService
   * @param {EndUserKeyMedia} keyMedia - Параметры ключа
   * @returns {Promise<PrivateKeyInfo>}
   */
  async readPrivateKeyUmCAService(keyMedia) {
    await this.setLibraryType(DigitalSignatureKeyType.UmCAService);

    const certs = [];
    for (const c of keyMedia.certificates) {
      const cert = await this.parseCertificate(c);
      certs.push(cert);
    }

    try {
      const signType = new EndUserSignContainerInfo();
      signType.type = EndUserConstants.EndUserSignContainerType.CAdES;
      signType.subType = EndUserConstants.EndUserCAdESType.Enveloped;
      signType.signLevel = EndUserConstants.EndUserSignType.CAdES_BES;

      const sign = await this.UmCAService.signData(keyMedia, "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=", signType);
      const info = await this._euSignFile.VerifyDataInternal(sign);
      this._readedKey = new PrivateKeyInfo(DigitalSignatureKeyType.UmCAService, info[0].ownerInfo, certs, keyMedia.visibleName, keyMedia.alias);
      this._readedKey.glSign = this.glSign;
      this._readedKey.keyMedia = keyMedia;
      return this._readedKey;
    } catch (error) {
      if (error.message && error.message == "KeyStoreException: KSE_INVALID_PASSWORD") {
        throw {
          code: EndUserError.EU_ERROR_BAD_PRIVATE_KEY,
          message: this._resourses.UmCAServiceWrongPassword
        };
      } else { throw error; }
    }
  }

  async getKeyMediasUmCAService() {
    await this.setLibraryType(DigitalSignatureKeyType.UmCAService);
    return this.UmCAService.getKeys();
  }

  /**
   * Считать файловый ключ c хранилища сертификатов Windows помощью расширения для браузера Signer.Digital Digital Signature
   * https://chromewebstore.google.com/detail/signerdigital-digital-sig/glghokcicpikglmflbbelbgeafpijkkf
   * Сделано по заказу от Аваль
   */
  async readWindowsCertStorePrivateKey() {
    await this.setLibraryType(DigitalSignatureKeyType.File);

    let keyInfo;

    try {
      // eslint-disable-next-line no-undef
      keyInfo = await SignerDigital.getSelectedCertificate();
    } catch (error) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PRIVATE_KEY,
        message: String(error)
      };
    }

    keyInfo = JSON.parse(keyInfo);
    const certInfo = await this.parseCertificate(keyInfo.Cert);

    this._readedKey = new PrivateKeyInfo(DigitalSignatureKeyType.WindowsCertStore, certInfo.infoEx, [certInfo], certInfo.infoEx.subjCN, keyInfo.CertThumbPrint);
    this._readedKey._glSign = this._glSign;
    return this._readedKey;
  }

  /**
   * Получить список облачных провайдеров
   * @returns {KSPSettings[]} Список поддержирживаемых облачных провайдеров
   */
  get KSPs() {
    return this._glSign.KSPs;
  }

  /**
   * Проверить, считан ли ключ
   * @returns {Promise<boolean>} считан ли ключ
   */
  async isPrivateKeyReaded() {
    if (!this._readedKey) {
      return false;
    }
    if (this._readedKey.keyType === DigitalSignatureKeyType.WindowsCertStore) {
      return true;
    }
    if (this._readedKey.keyType === DigitalSignatureKeyType.UmCAService) {
      return true;
    }
    try {
      return await this._euSign.IsPrivateKeyReaded();
    } catch {
      return false;
    }
  }

  /**
   * Очистить считанный ключ
   */
  async resetPrivateKey() {
    await this._euSign.ResetPrivateKey();
    this._readedKey = null;
  }

  /**
   * Остановить операцио с облачным ключом
   */
  async resetKSPOperation() {
    await this._euSignFile.ResetOperationKSP();
  }

  /**
   * Получить список центров сертификации
   * @returns {Promise<Array<CASettings>>}
   */
  async getCAs() {
    return await this._euSignFile.GetCAs();
  }

  /**
   * Установить выбранный центр сертификации. null для автоматического определения
   * @param {CASettings | string | null} CA - Параметры центра сертификации или его имя. null для автоматического определения
   */
  async setCA(CA) {
    if (typeof CA === "string") {
      const CAs = await this.getCAs();
      CA = CAs.find(ca => ca.issuerCNs.find(issuerCN => issuerCN === CA));
    }
    this._selectedCA = CA;
  }

  /**
   * Проверить, нужно ли указывать сертификаты ключа
   */
  needPrivateKeyCertificates() {
    return this._selectedCA && !this._selectedCA.cmpAddress;
  }

  /**
   * Подписать данные
   * @param {Uint8Array | string | NamedData | Array<Uint8Array | string | NamedData>} data - Данные для подписи. Можно передавать данные в массиве для наложения нескольких подписей за раз
   * @param {EndUserSignContainerInfo?} signType - Тип подписи
   * @param {boolean?} asByteArray - Возвращать подпись в виде массива байтов. По умолчанию подпись возвращается в виде строки в base64
   */
  async signData(data, signType, asByteArray) {
    if (signType === true) {
      // Обратная совместимость. Если передать в параметре значение true, дожна создаться подпись CAdES Enveloped (внутренняя)
      signType = new EndUserSignContainerInfo();
      signType.type = EndUserConstants.EndUserSignContainerType.CAdES;
      signType.subType = EndUserConstants.EndUserCAdESType.Enveloped;
      signType.signLevel = EndUserConstants.EndUserSignType.CAdES_X_Long;
    } else if (isDefaultValue(signType)) {
      // По умолчанию создаётся подпись CAdES Detached
      signType = new EndUserSignContainerInfo();
      signType.type = EndUserConstants.EndUserSignContainerType.CAdES;
      signType.subType = EndUserConstants.EndUserCAdESType.Detached;
      signType.signLevel = EndUserConstants.EndUserSignType.CAdES_X_Long;
    }
    if (typeof asByteArray !== "boolean") {
      asByteArray = false;
    }

    if (this.readedKey.keyType === DigitalSignatureKeyType.KSP) {
      if (signType.type !== EndUserConstants.EU_SIGN_CONTAINER_TYPE_CADES &&
        signType.type !== EndUserConstants.EU_SIGN_CONTAINER_TYPE_PADES) {
        throw {
          code: EndUserError.EU_ERROR_NOT_SUPPORTED,
          message: this._resourses.KSPSignFormatError
        };
      }

      if (signType.type === EndUserConstants.EU_SIGN_CONTAINER_TYPE_PADES) {
        const ksp = typeof this.readedKey.ksp === "object" ? this.readedKey.ksp.ksp : this.readedKey.ksp;
        if (ksp != EndUserKSP.GRYADA) {
          throw {
            code: EndUserError.EU_ERROR_NOT_SUPPORTED,
            message: this._resourses.KSPSignPAdESError
          };
        }
      }
    }

    if (this.readedKey.keyType === DigitalSignatureKeyType.WindowsCertStore) {
      if (signType.type !== EndUserConstants.EU_SIGN_CONTAINER_TYPE_CADES) {
        throw {
          code: EndUserError.EU_ERROR_NOT_SUPPORTED,
          message: this._resourses.KSPSignFormatError
        };
      }

      const hash = await this._euSign.HashData(this._readedKey.getHashAlgo(), data, !asByteArray);
      const signedData = await this._signHashWindowsCertificateStore(hash, signType, asByteArray);

      return this._euSign.m_library.m_worker.postMessage("AppendDataToSignedData", [signedData, data, !asByteArray]);
    }

    if (this.readedKey.keyType === DigitalSignatureKeyType.UmCAService) {
      if (signType.type !== EndUserConstants.EU_SIGN_CONTAINER_TYPE_CADES
        && signType.type !== EndUserConstants.EU_SIGN_CONTAINER_TYPE_PADES
      ) {
        throw {
          code: EndUserError.EU_ERROR_NOT_SUPPORTED,
          message: this._resourses.KSPSignFormatError
        };
      }
      return await this.UmCAService.signData(this._readedKey.keyMedia, data, signType, asByteArray);
    }

    const signAlgo = this._readedKey.getSignAlgo();

    if (signType.type === EndUserConstants.EndUserSignContainerType.ASiC) {
      const signLevel = signType.signLevel || signType.asicSignType === EndUserConstants.EndUserASiCSignType.CAdES ? EndUserConstants.EndUserSignType.CAdES_X_Long : EndUserConstants.EndUserXAdESSignLevel.B_LT;
      return await this._euSign.ASiCSignData(signAlgo, signType.subType, signType.asicSignType, signLevel, data, !asByteArray);
    } else if (signType.type === EndUserConstants.EndUserSignContainerType.XAdES) {
      let returnArray = true;
      if (!Array.isArray(data)) {
        data = [data];
        returnArray = false;
      }
      data = data.map((e, i) => !e.name && !e.val ? { name: "data" + i, val: e } : e);
      const signLevel = signType.signLevel || EndUserConstants.EndUserXAdESSignLevel.B_LT;
      const signs = [];
      for (let i = 0; i < data.length; i++) {
        const result = await this._euSign.XAdESSignData(signAlgo, signType.subType, signLevel, data[i], !asByteArray);
        signs[i] = result;
      }
      return returnArray ? signs : signs[0];
    } else if (signType.type === EndUserConstants.EndUserSignContainerType.PAdES) {
      const signLevel = signType.signLevel || EndUserConstants.EndUserPAdESSignLevel.B_T;
      return await this._euSign.PDFSignData(signAlgo, data, signLevel, !asByteArray);
    } else if (signType.type === EndUserConstants.EndUserSignContainerType.CAdES) {
      const signLevel = signType.signLevel || EndUserConstants.EndUserSignType.CAdES_X_Long;

      await this._euSign.SetRuntimeParameter(EndUserConstants.EU_SIGN_TYPE_PARAMETER, signLevel);

      if (signType.subType === EndUserConstants.EndUserCAdESType.Detached) {
        const hash = await this._euSign.HashData(this._readedKey.getHashAlgo(), data, !asByteArray);
        if (this._readedKey.keyType == DigitalSignatureKeyTypeWindowsCertStore) {
          return this._signHashWindowsCertificateStore(hash, signType, asByteArray);
        }
        return await this._euSign.SignHash(signAlgo, hash, true, !asByteArray);
      } else {
        return await this._euSign.SignDataEx(signAlgo, data, false, true, !asByteArray);
      }
    } else {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.BadSignatureType
      };
    }
  }

  /**
   * Подписать файл
   * @param {string | NamedData | Array<string | NamedData>} fileUrl - Ссылка на загрузку файла. Можно передавать несколько ссылок в массиве для наложения нескольких подписей за раз
   * @param {EndUserSignContainerInfo?} signType - Тип подписи
   * @param {boolean?} asByteArray - Возвращать подпись в виде массива байт. По умолчанию подпись возвращается в виде строки в base64
   */
  async signFile(fileUrl, signType, asByteArray) {
    return await downloadAndSignFiles(fileUrl, async (data) => await this.signData(data, signType, asByteArray));
  }

  /**
   * Подписать хеш подписью CAdES Detached
   * @param {Uint8Array | string | NamedData | Array<Uint8Array | string> | NamedData} hash - Хеш файла. Можно передавать несколько хешей в массиве для наложения нескольких подписей за раз
   * @param {boolean?} asByteArray - Возвращать подпись в виде массива байт. По умолчанию подпись возвращается в виде строки в base64
   */
  async signHash(hash, asByteArray) {
    if (typeof asByteArray !== "boolean") {
      asByteArray = false;
    }

    if (this._readedKey.keyType == DigitalSignatureKeyType.WindowsCertStore) {
      return this._signHashWindowsCertificateStore(hash, false, asByteArray);
    }
    if (this._readedKey.keyType == DigitalSignatureKeyType.UmCAService) {
      throw {
        code: EndUserError.EU_ERROR_NOT_SUPPORTED,
        message: this._resourses.KSPSignFormatError
      };
    }

    return await this._euSign.SignHash(this._readedKey.getSignAlgo(), hash, true, !asByteArray);
  }

  /**
   * Подписать хеш из файла подписью CAdES Detached
   * @param {string | NamedData | Array<string | NamedData>} hashUrl - Ссылка на скачивание хеша. Можно передавать несколько ссылок в массиве для наложения нескольких подписей за раз
   * @param {boolean?} asByteArray - Возвращать подпись в виде массива байт. По умолчанию подпись возвращается в виде строки в base64
   */
  async signFileHash(hashUrl, asByteArray) {
    return await downloadAndSignFiles(hashUrl, async (hash) => await this.signHash(hash, asByteArray));
  }

  /**
   * Получить данные о типе подписи
   * @param {string | Uint8Array} sign - Подпись
   */
  async getSignContainerInfo(sign) {
    return await this._euSign.GetSignContainerInfo(sign);
  }

  /**
   * Проверить подпись (Соответствие подписанных данных не проверяется)
   * @param {Uint8Array | string} data - Подписанные данные
   * @param {Uint8Array | string} sign - Подпись
   * @param {number?} signIndex - Номер подписи. -1 что бы проверить все подписи
   */
  async verifyData(data, sign, signIndex) {
    if (!Number.isInteger(signIndex)) {
      signIndex = -1;
    }
    const signContainerInfo = await this._euSign.GetSignContainerInfo(sign.val || sign);

    if (signContainerInfo.type === EndUserConstants.EndUserSignContainerType.ASiC) {
      return await this._euSign.ASiCVerifyData(sign.val || sign, signIndex);
    } else if (signContainerInfo.type === EndUserConstants.EndUserSignContainerType.PAdES) {
      return await this._euSign.PDFVerifyData(sign.val || sign, signIndex);
    } else if (signContainerInfo.type === EndUserConstants.EndUserSignContainerType.XAdES) {
      return await this._euSign.XAdESVerifyData(data, sign.val || sign, signIndex);
    } else if (signContainerInfo.type === EndUserConstants.EndUserSignContainerType.CAdES) {
      if (!data) {
        if (signContainerInfo.subType === EndUserConstants.EndUserCAdESType.Detached) {
          throw {
            code: EndUserError.EU_ERROR_BAD_PARAMETER,
            message: this._resourses.BadParameter + " data"
          };
        }
        return await this._euSign.VerifyDataInternal(sign.val || sign, signIndex);
      }
    }
    return await this._euSign.VerifyData(data.val || data, sign.val || sign, signIndex);
  }

  /**
   * Проверить внешнюю подпись файла
   * @param {string} fileUrl - Ссылка на загрузку файла
   * @param {string} signUrl - Ссылка на загрузку подписи
   * @param {number?} signIndex - Номер подписи. -1 что бы проверить все подписи
   */
  async verifyFile(fileUrl, signUrl, signIndex) {
    const [data, sign] = await Promise.all([
      downloadData(fileUrl, "binary"),
      downloadData(signUrl, "binary")
    ]);
    return await this.verifyData(data, sign, signIndex);
  }

  /**
   * Проверить внутреннюю подпись
   * @param {Uint8Array | string} sign - подпись с данными
   * @param {number?} signIndex - Номер подписи. -1 что бы проверить все подписи
   */
  async verifyDataInternal(sign, signIndex) {
    return await this.verifyData(null, sign, signIndex);
  }

  /**
   * Проверить внутреннюю подпись файла
   * @param {string} file - Ссылка на загрузку файла с подписью
   * @param {number?} signIndex - Номер подписи. -1 что бы проверить все подписи
   */
  async verifyFileInternal(fileUrl, signIndex) {
    const sign = await downloadData(fileUrl);
    return await this.verifyDataInternal(sign, signIndex);
  }

  /**
   * Проверить подпись хеша (Только CAdES Detached)
   * @param {Uint8Array | string} hash - Хеш подписанного файла
   * @param {Uint8Array | string} sign - Подпись
   * @param {number?} signIndex - Номер подписи. -1 что бы проверить все подписи
   */
  async verifyHash(hash, sign, signIndex) {
    if (!Number.isInteger(signIndex)) {
      signIndex = -1;
    }
    return await this._euSign.VerifyHash(hash.val || hash, sign.val || sign, signIndex);
  }

  /**
   * Проверить подпись хеша из файла (Только CAdES Detached)
   * @param {string} hashUrl - Ссылка на загрузку файла
   * @param {string} signUrl - Ссылка на загрузку подписи
   * @param {number?} signIndex - Номер подписи. -1 что бы проверить все подписи
   */
  async verifyFileHash(hashUrl, signUrl, signIndex) {
    const [data, sign] = await Promise.all([
      downloadData(hashUrl, "binary"),
      downloadData(signUrl, "binary")
    ]);
    return await this.verifyData(data, sign, signIndex);
  }

  /**
   * Выполнить подписание данных с проверкой подписи
   * @param {Uint8Array | string | NamedData | Array<Uint8Array | string | NamedData>} data - Данные для подписи. Можно передавать данные в массиве для наложения нескольких подписей за раз
   * @param {EndUserSignContainerInfo?} signType - Тип подписи
   */
  async signDataEx(data, signType) {
    const signs = await this.signData(data, signType);

    if (Array.isArray(data)) {
      const result = [];
      for (let i = 0; i < signs.length; i++) {
        result[i] = await this._formatSignInfo(data[i], signs[i], false);
      }
      return result;
    } else {
      return await this._formatSignInfo(data, signs);
    }
  }

  /**
   * Выполнить подписание с проверкой подписи
   * @param {string | NamedData | Array<string> | Array<NamedData>} fileUrl - ссылка на загрузку файла для подписания
   * @param {EndUserSignContainerInfo?} signType - Тип подписи
   */
  async signFileEx(fileUrl, signType) {
    return await downloadAndSignFiles(fileUrl, async (data) => await this.signDataEx(data, signType));
  }

  /**
   * Выполнить подписание хеша с проверкой подписи
   * @param {string | NamedData} hash - хеш для подписания
   */
  async signHashEx(hash) {
    const signs = await this.signHash(hash);

    if (Array.isArray(hash)) {
      const result = [];
      for (let i = 0; i < signs.length; i++) {
        result[i] = await this._formatSignInfo(hash[i], signs[i], true);
      }
      return result;
    } else {
      return await this._formatSignInfo(hash, signs, true);
    }
  }

  async _formatSignInfo(data, sign, isHash) {
    const that = this;
    return await getSignInfo(data.val || data, sign.val || sign);

    async function getSignInfo(data, sign) {
      let signInfo;
      if (isHash) {
        signInfo = await that.verifyHash(data, sign);
      } else {
        signInfo = await that.verifyData(data, sign);
      }
      if (Array.isArray(signInfo)) {
        signInfo = signInfo.pop();
      }

      const signer = await getSigner(sign);
      signInfo.ownerInfo.certBeginTime = dateWithoutTimezone(signer.certBeginTime);
      signInfo.ownerInfo.certEndTime = dateWithoutTimezone(signer.certEndTime);
      signInfo.ownerInfo.publicKeyType = signer.publicKeyType;
      signInfo.ownerInfo.keyUsageType = signer.keyUsageType;
      signInfo.ownerInfo.certHashType = that._readedKey.getHashAlgo();

      const timeInfo = signInfo.timeInfo
      const time = dateWithoutTimezone(timeInfo.isSignTimeStampAvail ? timeInfo.signTimeStamp : timeInfo.time);
      if (timeInfo.isSignTimeStampAvail || timeInfo.isTimeStamp) {
        signInfo.ownerInfo.timestamp = dateWithoutTimezone(timeInfo.isSignTimeStampAvail ? timeInfo.signTimeStamp : timeInfo.time);
      }

      switch (that._readedKey.keyType) {
        case DigitalSignatureKeyType.File:
          signInfo.ownerInfo.privateKeyType = "FileKey";
          break;
        case DigitalSignatureKeyType.Token:
          signInfo.ownerInfo.privateKeyType = "HardwareKey";
          break;
        case DigitalSignatureKeyType.UmCAService:
          signInfo.ownerInfo.privateKeyType = "UmCAService";
          break;
        case DigitalSignatureKeyType.WindowsCertStore:
          signInfo.ownerInfo.privateKeyType = "WindowsCertStore";
          break;
        case DigitalSignatureKeyType.KSP:
          signInfo.ownerInfo.privateKeyType = that._readedKey.ksp.ksp != EndUserKSP.GRYADA 
            ? that._readedKey.ksp.id:
            "GRYADA";
          break;
      }

      return {
        Success: true,
        Sign: sign,
        SignatureInfo: {
          Success: true,
          DateTimeStr: time,
          Signer: signInfo.ownerInfo.subjCN,
          OwnerInfo: signInfo.ownerInfo
        }
      };
    }
    async function getSigner(sign) {
      const containerInfo = await that._euSign.GetSignContainerInfo(sign); 
      let signerInfo;
      switch (containerInfo.type) {
        case EndUserConstants.EU_SIGN_CONTAINER_TYPE_CADES:
          signerInfo = await that._euSign.GetSigner(sign);
          break;
        case EndUserConstants.EU_SIGN_CONTAINER_TYPE_XADES:
          signerInfo = await that._euSign.XAdESGetSigner(sign);
          break;
        case EndUserConstants.EU_SIGN_CONTAINER_TYPE_PADES:
          signerInfo = await that._euSign.PDFGetSigner(sign);
          break;
        case EndUserConstants.EU_SIGN_CONTAINER_TYPE_ASIC:
          signerInfo = await that._euSign.ASiCGetSigner(sign);
          break;
      }
      if (Array.isArray(signerInfo)) {
        signerInfo = signerInfo.pop();
      }
      return signerInfo.infoEx;
    }
  }

  /**
   * Выполнить подписание данных через расширение для браузера Signer.Digital Digital Signature
   * https://chromewebstore.google.com/detail/signerdigital-digital-sig/glghokcicpikglmflbbelbgeafpijkkf
   * Сделано по заказу Аваль
   * @param {Uint8Array | string | NamedData | Array<Uint8Array | string | NamedData>} data - Данные для подписи. Можно передавать данные в массиве для наложения нескольких подписей за раз
   * @param {EndUserSignContainerInfo?} signType - Тип подписи
   */
  async _signHashWindowsCertificateStore(hash, signType, asByteArray) {
    if (typeof asByteArray !== "boolean") {
      asByteArray = false;
    }

    if (signType === true) {
      // Обратная совместимость. Если передать в параметре значение true, дожна создаться подпись CAdES Enveloped (внутренняя)
      signType = new EndUserSignContainerInfo();
      signType.type = EndUserConstants.EndUserSignContainerType.CAdES;
      signType.subType = EndUserConstants.EndUserCAdESType.Enveloped;
      signType.signLevel = EndUserConstants.EndUserSignType.CAdES_X_Long;
    } else if (isDefaultValue(signType)) {
      // По умолчанию создаётся подпись CAdES Detached
      signType = new EndUserSignContainerInfo();
      signType.type = EndUserConstants.EndUserSignContainerType.CAdES;
      signType.subType = EndUserConstants.EndUserCAdESType.Detached;
      signType.signLevel = EndUserConstants.EndUserSignType.CAdES_X_Long;
    }

    if (signType.type != EndUserConstants.EndUserSignContainerType.CAdES) {
      throw {
        code: EndUserError.EU_ERROR_NOT_SUPPORTED,
        message: this._resourses.KSPSignFormatError
      };
    }
    let hashType = "SHA256";

    switch (this._readedKey.getHashAlgo()) {
      case EndUserConstants.EU_CTX_HASH_ALGO_SHA512:
        hashType = "SHA512";
        break;
      case EndUserConstants.EU_CTX_HASH_ALGO_SHA384:
        hashType = "SHA384";
        break;
      case EndUserConstants.EU_CTX_HASH_ALGO_SHA256:
        hashType = "SHA256";
        break;
      case EndUserConstants.EU_CTX_HASH_ALGO_SHA224:
        hashType = "SHA224";
        break;
      case EndUserConstants.EU_CTX_HASH_ALGO_SHA160:
        hashType = "SHA160";
        break;
    }

    const that = this;
    async function signFunc(signValue) {
      if (typeof signValue != "string") {
        signValue = convertToBase64(signValue);
      }

      let signedData;

      try {
        const signAlgo = 2;
        // eslint-disable-next-line no-undef
        signedData = await SignerDigital.signHashCms(signValue, hashType, signAlgo, that._readedKey.id);
      } catch (error) {
        throw {
          code: EndUserError.EU_ERROR_BAD_CERT,
          message: String(error)
        };
      }

      return await that._euSign.m_library.m_worker.postMessage("AppendValidationData",
        [signedData, that._readedKey.certificates[0].data, that._readedKey.getSignAlgo(), !asByteArray]);
    }

    async function namedDataFunction(hash) {
      if (hash.val) {
        return { name: hash.name, val: await signFunc(hash.val) };
      }
      return await signFunc(hash);
    }

    if (Array.isArray(hash)) {
      const results = [];
      for (let i = 0; i < hash.length; i++) {
        const hashValue = hash[i];
        results[i] = await namedDataFunction(hashValue);
      }
      return results;
    } else {
      return await namedDataFunction(hash);
    }

  }

  /**
   * Зашифровать данные
   * @param {string | Uint8Array | string[] | Uint8Array[]} data - Данные, которые необходимо зашифровать
   * @param {Uint8Array[]} recipientCertificates - Сертификаты получателей
   * @param {boolean?} asByteArray - Возвращать результат в виде массива байтов. По умолчанию результат возвращается в виде строки BASE64
   * @returns {Promise<string | Uint8Array | string[] | Uint8Array[]>}
   */
  async envelopData(data, recipientCertificates, asByteArray) {
    if (typeof asByteArray !== "boolean") {
      asByteArray = false;
    }
    if (Array.isArray(data)) {
      return await Promise.all(data.map(e => this._euSign.EnvelopData(recipientCertificates, e, false, true, !asByteArray, false)));
    }
    return await this._euSign.EnvelopData(recipientCertificates, data, false, true, !asByteArray, false);
  }

  /**
   * Расшифровать данные
   * @param {string|Uint8Array} envelopedData - Зашифрованные данные в виде строки BASE64 или массива байт
   * @param {Uint8Array?} senderCert - Сертификат отправителя
   */
  async developData(envelopedData, senderCert) {
    return await this._euSign.DevelopData(envelopedData, senderCert);
  }

  /**
   * Зашифровать файл
   * @param {string | string[]} fileUrl - Ссылка на загрузку файла
   * @param {Uint8Array[]} recipientCertificates - Сертификаты получателей
   * @param {boolean} asByteArray - Возвращать результат в виде массива байтов. По умолчанию результат возвращается в виде строки BASE64
   */
  async envelopFile(fileUrl, recipientCertificates, asByteArray) {
    let data;
    if (Array.isArray(fileUrl)) {
      data = await Promise.all(fileUrl.map(url => downloadData(url, "binary")));
    } else {
      data = await downloadData(fileUrl, "binary");
    }
    return await this.envelopData(data, recipientCertificates, asByteArray);
  }

  /**
   * Расшифровать файл
   * @param {string} envelopedFileUri - Ссылка на зашифрованный файл
   * @param {Uint8Array?} senderCert - Сертификат отправителя
   */
  async developFile(envelopedFileUri, senderCert) {
    const data = await downloadData(envelopedFileUri, "binary");
    return await this.developData(data, senderCert);
  }

  /**
   * Зашифрувати звіти в податкову
   * @param {ProtectTaxReportParams} params - Параметри шифрування
   * @returns {Promise<Array<EndUserTaxReport>>}
   */
  async protectTaxReports(params) {
    return await this._euSign.ProtectTaxReports(
      params.accountantPrivateKeyContext?.keyContext,
      Array.isArray(params.directorPrivateKeyContext) ? params.directorPrivateKeyContext.map(e => e.keyContext) : params.directorPrivateKeyContext?.keyContext,
      Array.isArray(params.stampPrivateKeyContext) ? params.stampPrivateKeyContext.map(e => e.keyContext) : params.stampPrivateKeyContext?.keyContext,
      params.senderEMail,
      params.recipientCertificate,
      params.reports,
      params.returnIntermediateSign);
  }

  /**
   * Считать аппаратный ключ в контекст
   * @param {EndUserKeyMedia} keyMedia - Параметры аппаратного ключа
   * @param {Array<Uint8Array>?} certs - Сертификаты ключа (при необходимости)
   */
  async ctxReadHardwareKey(keyMedia, certs) {
    await this.setLibraryType(DigitalSignatureKeyType.Token);

    if (!keyMedia) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.BadParameter + " keyMedia"
      };
    }
    if (!keyMedia.password) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.PasswordNotSet
      };
    }

    if (Array.isArray(certs) && certs.length > 0) {
      certs = await Promise.all(certs.map(cert =>
        cert instanceof File || cert instanceof Blob ? readFile(cert) :
          cert instanceof EndUserCertificate ? cert.data :
            cert));
    }

    try {
      const ctx = await this._euSign.CtxCreate();
      const keyCtx = await this._euSign.CtxReadPrivateKey(ctx, keyMedia, certs, this._selectedIssuerCN);
      const readedKey = new HardwarePrivateKeyInfo(
        DigitalSignatureKeyType.Token,
        keyCtx.ownerInfo,
        await this._euSign.CtxGetOwnCertificates(keyCtx),
        keyMedia
      );
      readedKey.context = ctx;
      readedKey.keyContext = keyCtx;
      readedKey._glSign = this._glSign;
      return readedKey;
    } catch (error) {
      if (error && error.code === EndUserError.EU_ERROR_CERT_NOT_FOUND) {
        if (!this._selectedIssuerCN) {
          throw {
            code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
            message: this._resourses.ReadPrivateKeyCAAutoDetectError
          };
        } else if (this._selectedIssuerCN) {
          if (!this._selectedCA.cmpAddress) {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyNeedCertificateError, this._selectedIssuerCN)
            };
          } else {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyInvalidCAError, this._selectedIssuerCN)
            };
          }
        }
      }
      throw error;
    }
  }

  /**
   * Считать файловый ключ в контекст
   * @param {Uint8Array|File} privateKey - Файловый ключ
   * @param {string} password - Пароль от ключа
   * @param {Array<Uint8Array|File>?} certs - Сертификаты ключа (при необходимости)
   */
  async ctxReadFileKey(privateKey, password, certs) {
    await this.setLibraryType(DigitalSignatureKeyType.File);

    if (!privateKey) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.BadParameter + " keyMedia"
      };
    }
    if (!password) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: this._resourses.PasswordNotSet
      };
    }

    if (privateKey instanceof File || privateKey instanceof Blob) {
      privateKey = await readFile(privateKey);
    }
    if (Array.isArray(certs) && certs.length > 0) {
      certs = await Promise.all(certs.map(cert =>
        cert instanceof File || cert instanceof Blob ? readFile(cert) :
          cert instanceof EndUserCertificate ? cert.data :
            cert));
    }
    try {
      const ctx = await this._euSign.CtxCreate();
      const keyCtx = await this._euSign.CtxReadPrivateKeyBinary(ctx, privateKey, password, certs, this._selectedIssuerCN);
      const readedKey = new FilePrivateKeyInfo(
        DigitalSignatureKeyType.File,
        keyCtx.ownerInfo,
        await this._euSign.CtxGetOwnCertificates(keyCtx),
        privateKey,
        password
      );
      readedKey.context = ctx;
      readedKey.keyContext = keyCtx;
      readedKey._glSign = this._glSign;
      return readedKey;
    } catch (error) {
      if (error && error.code === EndUserError.EU_ERROR_CERT_NOT_FOUND) {
        if (!this._selectedIssuerCN) {
          throw {
            code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
            message: this._resourses.ReadPrivateKeyCAAutoDetectError
          };
        } else if (this._selectedIssuerCN) {
          if (!this._selectedCA.cmpAddress) {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyNeedCertificateError, this._selectedIssuerCN)
            };
          } else {
            throw {
              code: EndUserError.EU_ERROR_CERT_NOT_FOUND,
              message: format(this._resourses.ReadPrivateKeyInvalidCAError, this._selectedIssuerCN)
            };
          }
        }
      }
      throw error;
    }
  }

  /**
   * Освободить контекст приватного ключа
   * @param {PrivateKeyInfo} privateKeyContext - Контекст приватного ключа
   */
  async ctxFreePrivateKey(privateKeyContext) {
    if (privateKeyContext.keyType === DigitalSignatureKeyType.Token) {
      await this.setLibraryType(DigitalSignatureKeyType.Token);
    } else {
      await this.setLibraryType(DigitalSignatureKeyType.File);
    }

    await this._euSign.CtxFreePrivateKey(privateKeyContext.keyContext);
    await this._euSign.CtxFree(privateKeyContext.context);
  }

  async getPrivateKeyInfoFromStorage(fromLocalStorage, keyType) {
    const storage = fromLocalStorage ? localStorage : sessionStorage;
    const storedKeys = storage[this._userId + PRIVATE_KEY_INFO];

    let ls;
    const result = [];
    if (!storedKeys) {
      return result;
    }
    const data = await this._euSignFile.UnprotectDataByPassword(storedKeys, "", true);
    try {
      ls = JSON.parse(data);
      if (keyType >= 0) {
        ls = ls.filter(key => key.keyType == keyType);
      }
    }
    catch {
      ls = [];
    }

    ls.forEach(key => {
      const password = key.password;
      if (key.keyType == DigitalSignatureKeyType.File) {
        key.privateKey = new Uint8Array(Object.assign(new Array(), key.privateKey));
        key = new FilePrivateKeyInfo(key.keyType, key.ownerInfo, key.certificates, key.privateKey, key.password);
      }
      else if (key.keyType == DigitalSignatureKeyType.Token) {
        key = new HardwarePrivateKeyInfo(key.keyType, key.ownerInfo, key.certificates, key.keyMedia);
      }
      else if (key.keyType == DigitalSignatureKeyType.KSP) {
        key = new KspPrivateKeyInfo(key.keyType, key.ownerInfo, key.certificates, key.userId, key.ksp, key.keyId);
      }
      else if (key.keyType == DigitalSignatureKeyType.UmCAService) {
        const keyMedia = key.keyMedia;
        key = new PrivateKeyInfo(key.keyType, key.ownerInfo, key.certificates, key.visibleName, key.id);
        key.keyMedia = keyMedia;
      }
      else if (key.keyType == DigitalSignatureKeyType.WindowsCertStore) {
        key = new PrivateKeyInfo(key.keyType, key.ownerInfo, key.certificates, key.visibleName, key.id);
      }
      const certs = [];
      key.certificates.forEach(cert => {
        const certObj = Object.assign(new EndUserCertificate(), cert);
        certObj.data = new Uint8Array(Object.assign(new Array(), cert.data));
        certs.push(certObj);
      });
      key.certificates = certs;
      key.password = password;
      key._glSign = this._glSign;
      result.push(key);
    });
    return result;
  }

  /**
   * Сохранить ключ
   * @param {PrivateKeyInfo} privateKeyInfo - Ключ
   * @param {boolean} toLocalStorage - Будет ли ключ сохранён после закрытия вкладки
   */
  async storePrivateKeyInfo(privateKeyInfo, toLocalStorage) {
    if (privateKeyInfo.ksp && privateKeyInfo.ksp.needQRCode) {
      return;
    }

    const storage = toLocalStorage ? localStorage : sessionStorage;
    const keys = await this.getStoredPrivateKeyInfo();

    if (keys.length == 0 || keys.filter(key => key.id == privateKeyInfo.id).length < 1) {
      const keys = await this.getPrivateKeyInfoFromStorage(toLocalStorage);
      keys.push(privateKeyInfo);
      const data = await this._euSignFile.ProtectDataByPassword(JSON.stringify(keys), "", true);
      storage[this._userId + PRIVATE_KEY_INFO] = data;
    }
  }

  /**
   * Получить сохранённые ключи
   * @param {number?} keyType - Вид ключа
   * @returns {Array<PrivateKeyInfo>} Сохранённые ключи
   */
  async getStoredPrivateKeyInfo(keyType) {
    const local = await this.getPrivateKeyInfoFromStorage(true, keyType);
    const session = await this.getPrivateKeyInfoFromStorage(false, keyType);
    return local.concat(session);
  }

  /**
   * Удалить сохранённые ключи
   * @param {string} keyId - Идентификатор ключа
   */
  async removeStoredPrivateKeyInfo(keyId) {
    if (keyId === undefined) {
      localStorage.removeItem(this._userId + PRIVATE_KEY_INFO);
      sessionStorage.removeItem(this._userId + PRIVATE_KEY_INFO);
    }
    else {
      const localStoredKeys = await this.getPrivateKeyInfoFromStorage(true);
      const sessionStoredKeys = await this.getPrivateKeyInfoFromStorage(false);
      const storage = localStoredKeys.filter(item => item.id == keyId).length > 0 ? localStorage : sessionStorage;
      const keys = storage == localStorage ? localStoredKeys : sessionStoredKeys;
      const keyIndex = keys.findIndex((element) => element.id == keyId);

      if (keyIndex > -1) {
        keys.splice(keyIndex, 1);
        const data = await this._euSignFile.ProtectDataByPassword(JSON.stringify(keys), "", true);
        storage[this._userId + PRIVATE_KEY_INFO] = data;
      }
    }
  }

  /**
   * Получить предпочитаемый тип ключа
   */
  get _preferredKeyType() {
    const keyType = parseInt(localStorage[this._userId + PRIVATE_KEY_TYPE]);
    if (typeof keyType === "number" && keyType > -1) {
      return keyType;
    } else {
      localStorage[this._userId + PRIVATE_KEY_TYPE] = DigitalSignatureKeyType.File;
      return DigitalSignatureKeyType.File;
    }
  }

  /**
   * Задать предпочитаемый тип ключа
   * @param {DigitalSignatureKeyType | number} keyType - Тип ключа
   */
  set _preferredKeyType(keyType) {
    if (typeof keyType === "number" && keyType > -1) {
      localStorage[this._userId + PRIVATE_KEY_TYPE] = keyType;
    }
  }

  get _resourses() {
    return Resourses[this._language];
  }

  get _userId() {
    return typeof this._settingsProvider.userId === "function" ? this._settingsProvider.userId() : this._settingsProvider.userId;
  }

  get _language() {
    return typeof this._settingsProvider.language === "function" ? this._settingsProvider.language() : this._settingsProvider.language;
  }

  /**
   * Проверка доступности Дiя
   */
  async _diiaCheckAccess(ksp) {
    const diia = "DIIA";
    if (sessionStorage[diia] != "+") {
      try {
        sessionStorage[diia] = await downloadData(ksp.address.replace("KSPSign", "DIIA"));
      } catch {
        throw {
          code: EndUserError.EU_ERROR_TRANSMIT_REQUEST,
          message: this._resourses.DiiaError + `<a target="_blank" href="${window.origin}">${window.origin}</a></li><ul>`
        };
      }
    }
    try {
      const health = await downloadData(ksp.address.replace("KSPSign", "health"));
      if (health.trim().toLowerCase() != "healthy") {
        throw {
          code: EndUserError.EU_ERROR_TRANSMIT_REQUEST,
          message: this._resourses.DiiaHealthCheckError
        };
      }
    } catch (error) {
      console.error(error);
      throw {
        code: EndUserError.EU_ERROR_TRANSMIT_REQUEST,
        message: this._resourses.DiiaHealthCheckError
      };
    }

  }
}
